import { login, logout, query, getToken, getBranding, getApiUrl } from "sinao-corejs";

const actions = {
	async branding({ commit }) {
		const brand = await getBranding();
		var { icon, logo, name } = brand;

		if (brand.domain == "sinao.app") {
			logo = "/logo.svg";
			icon = "/icon.ico";
		}

		commit("SET_LOGO", logo);
		commit("SET_NAME", name);
		commit("SET_DOMAIN", brand.domain);
		commit("SET_IS_LICENSED", brand.is_licensed);
		commit("SET_ACCOUNT_MANAGER", brand.account_manager);

		const disable_messenger = brand.intercom_configuration?.disable_messenger || false;
		window.dataLayer.push({
			event: "intercom_ready",
			disable_messenger: disable_messenger,
			hostname_alias: brand.domain || null
		});

		const favicon = document.querySelector("link[rel='icon']");
		favicon.href = icon;

		return brand;
	},
	sendPassword(_, payload) {
		const { email, hostname_alias } = payload;

		return query({
			operationId: "auth.sendpassword",
			data: {
				email,
				hostname_alias
			}
		});
	},
	changePassword(_, payload) {
		const { password, forgotten_password_token } = payload;

		return query({
			operationId: "auth.changepassword",
			data: {
				password,
				forgotten_password_token
			}
		});
	},
	deleteInviteByToken(_, accessToken) {
		return query({
			operationId: "app.policies.registration.delete",
			params: {
				accessToken
			}
		});
	},
	createInvite(_, payload) {
		const { token, firstname, lastname, password, cgu } = payload;

		return query({
			operationId: "app.policies.registration.register",
			params: {
				accessToken: token
			},
			data: {
				firstname,
				lastname,
				password,
				cgu
			}
		});
	},
	async getInvite(_, accessToken) {
		const response = await query({
			operationId: "app.policies.registration.get",
			params: {
				accessToken,
				"expand[0]": "app",
				"expand[1]": "app.organization",
				"expand[2]": "recipient_user",
				"expand[3]": "sender_user"
			}
		});

		return response;
	},
	async getUser({ commit, dispatch }) {
		const response = await query(
			{
				operationId: "account.get",
				params: {
					"expand[0]": "collaborator_partner_id",
					"expand[1]": "collaborator_partner_profile",
					"expand[2]": "collaborator_hostname_alias"
				}
			},
			true
		);

		commit("SET_USER", response);
		window.dataLayer.push({
			event: "intercom_ready",
			user_id: `us_${response.id}`,
			user_hash: response.intercom_key,
			user_name: response.name,
			user_email: response.email
		});
	},
	async login(_, payload) {
		const { email, password, code, hostname_alias } = payload;

		const response = await query(
			{
				operationId: "auth.login",
				data: {
					email: email,
					password: password,
					code: code ?? null,
					hostname_alias: hostname_alias
				}
			},
			true
		);

		if (response.access_token) {
			return await login({
				token: response.access_token
			});
		}

		return response;
	},
	async logout({ commit }, token) {
		query(
			{
				operationId: "auth.logout",
				params: {
					token
				}
			},
			true
		);

		commit("SET_USER", null);
		commit("SET_LOGO", null);
		commit("SET_NAME", null);
		commit("SET_LOADED_APPS", null);
		commit("SET_LOADED_POLICIES", null);
		commit("SET_LOADED_CASES", null);
		commit("SET_PARTNER", null);
		commit("SET_LICENSE_PLANS", null);
		// commit("SET_ACCOUNTING_FIRM", null);

		logout();
	},
	async getApps() {
		const { data } = await query({
			operationId: "app.list",
			params: {
				"expand[0]": "organization",
				"expand[1]": "policies",
				"expand[2]": "policies.user",
				"expand[3]": "policies.profile",
				"expand[4]": "subscription",
				"expand[5]": "invites",
				"expand[6]": "organization.headquarters",
				"expand[7]": "admin",
				"expand[8]": "subscription.upcoming_invoice",
				limit: "1000"
			}
		});

		return data;
	},
	async getAppByAppId(_, appId) {
		const response = await query({
			operationId: "app.get",
			params: {
				appId,
				"expand[0]": "organization",
				"expand[1]": "policies",
				"expand[2]": "policies.user",
				"expand[3]": "policies.profile",
				"expand[4]": "invites",
				"expand[5]": "admin",
				"expand[6]": "subscription",
				"expand[7]": "invites.token"
			}
		});

		return response;
	},
	async createApp(_, params) {
		await query({
			operationId: "app.create",
			params: params /* organization_name + organization_size */
		});
	},
	async updateAppSettings(_, { appId, token, data }) {
		return query({
			operationId: "app.settings.update",
			token,
			params: {
				appId
			},
			data: {
				settings: {
					data
				}
			}
		});
	},
	async getPolicies({ commit }, appId) {
		const { data } = await query({
			operationId: "app.policies.profiles.list",
			params: { appId }
		});

		commit("SET_LOADED_POLICIES", data);
		return data;
	},
	async updatePolicy(_, payload) {
		const { appId, userId, policyProfileId } = payload;

		return await query({
			operationId: "app.policies.update",
			params: {
				appId,
				userId,
				policy_profile_id: policyProfileId
			}
		});
	},
	async deleteAccess(_, payload) {
		const { appId, userId } = payload;

		await query({
			operationId: "app.policies.delete",
			params: {
				appId,
				userId
			}
		});
	},
	async sendInviteMail(_, payload) {
		const { appId, email, policyProfileId, firstname, lastname, password } = payload;
		const _params = {
			appId,
			email,
			firstname,
			lastname,
			password,
			policy_profile_id: policyProfileId
		};
		if (payload.additionalsAppId) {
			_params.additionalsAppId = payload.additionalsAppId;
		}

		await query({
			operationId: "app.policies.invitations.create",
			params: { appId },
			data: _params
		});
	},
	async sendInviteCollaborator(_, payload) {
		return await query({
			operationId: "app.collaborators.add",
			params: {
				partnerId: payload.partnerId
			},
			data: payload
		});
	},
	async deleteInviteById(_, payload) {
		const { appId, id } = payload;

		return await query({
			operationId: "app.policies.invitations.delete",
			params: {
				appId,
				id
			}
		});
	},
	// async saveAccountingFirmUserPolicies(_, payload) {
	// 	const { userId, appId } = payload;
	// 	await query({
	// 		operationId: "app.policies.accounting_batch",
	// 		params: {
	// 			userId,
	// 			appId
	// 		}
	// 	});
	// },
	async sendCodeLogin(_, payload) {
		const { email, password, hostname_alias } = payload;

		return await query({
			operationId: "auth.send_2fa_code",
			data: {
				email,
				password,
				hostname_alias
			}
		});
	},
	async getPartnerCases({ commit }, payload) {
		const { partnerId, pageSize, search, offset, signal } = payload; // Accept signal
		const params = {
			partnerId,
			limit: pageSize,
			offset: offset,
			"expand[0]": "subscription",
			"expand[1]": "subscription.upcoming_invoice",
			"expand[2]": "policies",
			"expand[3]": "policies.user",
			// "expand[4]": "policies.profile"
			"expand[5]": "invites",
			"expand[6]": "admin"
		};

		if (search && search.length > 0) {
			params["filters[0][0][name]"] = "organization.name";
			params["filters[0][0][comparator]"] = "LIKE";
			params["filters[0][0][value]"] = `%${search}%`;

			params["filters[0][1][name]"] = "organization.national_id";
			params["filters[0][1][comparator]"] = "LIKE";
			params["filters[0][1][value]"] = `%${search}%`;
		}

		// NOT WORKING YET - BECAUSE query DOES'T SUPPORT SIGNAL
		// const { data, meta } = await query({
		// 	operationId: "app.partners.list",
		// 	params
		// });

		const apiUrl = await getApiUrl();
		const response = await fetch(`${apiUrl}/partners/${partnerId}/cases?` + new URLSearchParams(params), {
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getToken()}`
			},
			method: "GET",
			signal: signal
		});
		const { data, meta } = await response.json();

		// Filter out admin policies from each case item
		const processedData = data.map(caseItem => {
			if (caseItem.policies && caseItem.admin && caseItem.admin.id) {
				caseItem.policies = caseItem.policies.filter(
					policy => policy.user && policy.user.id !== caseItem.admin.id
				);
			}
			return caseItem;
		});

		commit("SET_LOADED_CASES", processedData);
		commit("SET_TOTAL_ROWS_LICENSE", meta.total_count);

		return processedData;
	},
	async getPartner({ commit }, payload) {
		const { partnerId } = payload;

		const response = await query({
			operationId: "app.partners.get_partner",
			params: {
				partnerId
			}
		});

		commit("SET_PARTNER", response);

		return response;
	},
	async getLicensePlans({ commit }, payload) {
		const { partnerId } = payload;

		const { data } = await query({
			operationId: "app.partners.plans",
			params: {
				partnerId
			}
		});

		commit("SET_LICENSE_PLANS", data);

		return data;
	},
	async getApikey(_, payload) {
		const { partnerId, app_id } = payload;

		const response = await query({
			operationId: "app.partners.apikey",
			params: {
				partnerId,
				app_id
			}
		});

		return response;
	},
	async updateSubscription({ commit }, payload) {
		const { partnerId, app_id, plan_nickname } = payload;

		const { data } = await query({
			operationId: "app.partners.update_subscription",
			params: {
				partnerId,
				app_id
			},
			data: {
				plan_nickname
			}
		});

		commit("UPDATE_SUBSCRIPTION", { app_id, subscription: data });

		return data;
	},
	async cancelSubscription({ commit }, payload) {
		const { partnerId, app_id } = payload;

		const { data } = await query({
			operationId: "app.partners.cancel_subscription",
			params: {
				partnerId,
				app_id
			}
		});

		commit("UPDATE_SUBSCRIPTION", { app_id, subscription: data });

		return data;
	},
	async assignSupportCollaborator(_, payload) {
		const { partnerId, id, userId } = payload;
		if (userId == null || userId === undefined) delete payload.userId;

		await query({
			operationId: "app.collaborators.assign_support",
			params: {
				partnerId,
				id: id
			},
			data: payload
		});
	},
	acceptInviteSupport(_, payload) {
		const { token, email, appId, partnerId } = payload;

		return query({
			operationId: "app.collaborators.accept_invite",
			params: {
				token,
				partnerId
			},
			data: {
				email,
				appId
			}
		});
	},
	async getCollaborators({ commit }, payload) {
		const { partnerId } = payload;

		const { data } = await query({
			operationId: "app.collaborators.list",
			params: {
				partnerId,
				"expand[0]": "user",
				"expand[1]": "policy_profile",
				"expand[2]": "apps_by_policies"
			}
		});

		// Keep apps that match the collaborator's policy profile ID
		const processedData = data.map(collaboratorItem => {
			if (collaboratorItem.apps_by_policies && Array.isArray(collaboratorItem.apps_by_policies)) {
				if (collaboratorItem.policy_profile_id) {
					collaboratorItem.apps_by_policies = collaboratorItem.apps_by_policies.filter(
						app => app && app.policy_profile_id === collaboratorItem.policy_profile_id
					);
				}
			}
			return collaboratorItem;
		});

		commit("SET_LOADED_COLLABORATORS", processedData);
		return processedData;
	},
	async deleteCollaborators(_, payload) {
		const { partnerId, ids } = payload;

		if (!ids || ids.length === 0) {
			throw new Error("No collaborator IDs provided for deletion.");
		}

		ids.forEach(async collaboratorId => {
			await query({
				operationId: "app.collaborators.remove",
				params: {
					partnerId,
					id: collaboratorId
				}
			});
		});
	},
	async deleteApps(_, payload) {
		const { partnerId, ids } = payload;

		await query({
			operationId: "app.partners.delete_apps",
			params: { partnerId, ids }
			// params: { partnerId },
			// data: { ids }
		});
	},
	async addPaymentMethod(_, payload) {
		const { app_id } = payload;

		const { uri } = await query({
			operationId: "app.subscription.checkout_add_source",
			params: { appId: app_id, redirect: "account" }
		});

		return uri;
	},
	async updateCollaboratorPolicy(_, payload) {
		const { collaboratorId, policyProfileId, partnerId } = payload;

		return await query({
			operationId: "app.collaborators.update",
			params: {
				partnerId,
				id: collaboratorId
			},
			data: {
				policy_profile_id: policyProfileId
			}
		});
	}
};

export default actions;
